import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgentInfo, Api, PermissionInfo, UserInfo } from '../api';
import { DEFAULT_PERMISSION, PERMISSION_OPTIONS, ROLE } from '../admin/labels';
import { confirmAction } from '../ui/confirm';
import { ALL_FEATURES, describeFeatures, featuresAtLevel } from '../features';

/** 옛 `admin` 값(이행 전 데이터)은 제어로 본다 — §14.19.2 에서 효과가 같아 정리됐다. */
const levelOf = (permissionType: string): string => (permissionType === 'admin' ? 'control' : permissionType);

/**
 * 사용자 한 명의 기기 접근 권한(2026-09-15 관리 화면). 서버 `GET/POST/DELETE /api/users/:id/permissions`.
 *
 * 일반 사용자는 **권한을 받은 기기만** 목록에 보이고 접속할 수 있다. 관리자는 조직의 모든 기기에 접근하므로
 * 권한 목록이 의미가 없다 → 안내만 보여 준다(주는 것은 막지 않지만, 효과가 없다는 것을 알려야 헷갈리지 않는다).
 */
export function PermissionPanel({ api, user }: { api: Api; user: UserInfo }) {
  const [perms, setPerms] = useState<PermissionInfo[]>([]);
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [addAgent, setAddAgent] = useState('');
  const [addType, setAddType] = useState(DEFAULT_PERMISSION);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, a] = await Promise.all([api.listPermissions(user.userId), api.getAgents()]);
      setPerms(p);
      setAgents(a);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api, user.userId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 아직 권한이 없는 기기만 "추가" 후보로 — 이미 있는 기기는 위 목록에서 유형을 바꾸면 된다. */
  const candidates = useMemo(() => {
    const granted = new Set(perms.map((p) => p.agentId));
    return agents.filter((a) => !granted.has(a.id));
  }, [agents, perms]);

  /** 부여·유형 변경(서버가 멱등 upsert) / 회수 — 공통 껍데기: 잠금 → 실행 → 재조회. */
  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true);
      setError('');
      try {
        await action();
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  // 저장된 허용 기능(옛 서버는 필드가 없다 → 전부).
  const userFeatures = user.features ?? ALL_FEATURES;

  if (user.role === ROLE.ADMIN) {
    return <div className="hint">관리자는 조직의 모든 기기에 접속할 수 있어 기기별 권한이 필요하지 않습니다.</div>;
  }

  return (
    <div>
      <div className="hint" style={{ marginBottom: 8 }}>
        이 사용자는 아래 기기만 목록에서 보고 접속할 수 있습니다. 기기마다 실제로 쓸 수 있는 기능 =
        사용자의 허용 기능({describeFeatures(userFeatures)}) 중 그 기기 수준이 허락하는 것(보기면 제어·클립보드·파일 전송 제외).
      </div>
      {error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 8 }}>{error}</div>}
      {loading && perms.length === 0 ? (
        <div className="hint">불러오는 중…</div>
      ) : perms.length === 0 ? (
        <div className="hint" style={{ marginBottom: 8 }}>아직 접속할 수 있는 기기가 없습니다.</div>
      ) : (
        <div className="list" style={{ marginBottom: 10 }}>
          {perms.map((p) => (
            <div key={p.agentId} className="list-item" style={{ cursor: 'default' }}>
              <div style={{ flex: 1 }}>
                <div className="name">{p.deviceName || '(이름 없음)'}</div>
                <div className="sub">
                  {p.deviceCode} · 쓸 수 있는 기능: {describeFeatures(featuresAtLevel(userFeatures, levelOf(p.permissionType)))}
                </div>
              </div>
              <select
                value={levelOf(p.permissionType)}
                disabled={busy}
                onChange={(e) => {
                  const next = e.target.value;
                  void run(() => api.grantPermission(user.userId, p.agentId, next));
                }}
                aria-label="권한 유형"
              >
                {PERMISSION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} title={o.hint}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                className="ghost"
                disabled={busy}
                onClick={async () => {
                  // 확인 받고 진행(사용자 요청 2026-09-27) — 회수하면 그 사용자는 이 기기에 더는 접속할 수 없다.
                  const ok = await confirmAction({
                    title: `${user.userName || user.email} — 이 기기의 접속 권한을 회수할까요?`,
                    message: '회수하면 이 사용자는 이 기기에 접속할 수 없습니다. 다시 부여할 수 있습니다.',
                    confirmLabel: '회수',
                    danger: true,
                  });
                  if (ok) void run(() => api.revokePermission(user.userId, p.agentId));
                }}
              >
                회수
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <select value={addAgent} onChange={(e) => setAddAgent(e.target.value)} disabled={busy} aria-label="기기 선택">
          <option value="">{candidates.length ? '기기 선택…' : '추가할 기기가 없습니다'}</option>
          {candidates.map((a) => (
            <option key={a.id} value={a.id}>
              {(a.deviceName || '(이름 없음)') + ` · ${a.deviceCode}`}
            </option>
          ))}
        </select>
        <select value={addType} onChange={(e) => setAddType(e.target.value)} disabled={busy} aria-label="권한 유형">
          {PERMISSION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} title={o.hint}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          disabled={busy || !addAgent}
          onClick={() =>
            void run(async () => {
              await api.grantPermission(user.userId, addAgent, addType);
              setAddAgent('');
            })
          }
        >
          권한 주기
        </button>
        <span className="hint">
          {PERMISSION_OPTIONS.find((o) => o.value === addType)?.hint}
        </span>
      </div>
    </div>
  );
}
